import { create } from 'zustand'
import type { FileArtifact } from '@/types/chat'

/**
 * Ephemeral UI state (plan §1): sidebar collapse, command palette, file
 * panel. Server state lives in TanStack Query; streaming state lives in
 * useChatStream.
 */

/** Right-hand aside (concept .file-preview): a files list, a single file,
 * or closed. Opening a file from the list keeps the list for "All files". */
export type FilePanelState =
  | { mode: 'closed' }
  | { mode: 'list'; files: FileArtifact[] }
  | { mode: 'file'; file: FileArtifact; list?: FileArtifact[] }

/** Right-hand query-results aside, opened by a code block's Run button. */
export interface QueryRunRequest {
  chatId: string
  sql: string
  queryEngineId: string
  queryEngineType: string
  title?: string
}
export type QueryPanelState =
  | { mode: 'closed' }
  | ({
      mode: 'open'
      /** Bumped on every run so re-clicking Run on the same SQL restarts it. */
      nonce: number
    } & QueryRunRequest)

interface UIState {
  sidebarCollapsed: boolean
  toggleSidebar: () => void
  setSidebarCollapsed: (v: boolean) => void

  commandOpen: boolean
  /** Bumped on every open so ⌘K while already open re-resets the palette. */
  commandNonce: number
  setCommandOpen: (v: boolean) => void

  settingsOpen: boolean
  setSettingsOpen: (v: boolean) => void
  /** Active settings nav section — in the store so entry points can deep
   * link (composer "Manage Skills" → Skills page, user 2026-07-28). */
  settingsSection: string
  setSettingsSection: (s: string) => void

  /** Model selector (concept #model-menu) — shared by every composer. */
  model: string
  setModel: (name: string) => void
  thinking: boolean
  toggleThinking: () => void

  /** Composer add-menu toggles (user-directed 2026-07-24): research shows a
   * blue pill beside the + button; skills are multi-select. */
  research: boolean
  toggleResearch: () => void
  skills: string[]
  toggleSkill: (name: string) => void

  /** Settings → Capabilities (user 2026-08-10): turning a knob off hides the
   * matching composer add-menu item (file uploads / screenshots). */
  capabilities: { fileUploads: boolean; screenshots: boolean }
  toggleCapability: (key: keyof UIState['capabilities']) => void

  /** UI language (user 2026-07-28). Selection only — translations land with
   * a later i18n phase; copy stays English until then. */
  language: string
  setLanguage: (code: string) => void

  /** Theme state: light, dark, or system */
  theme: 'light' | 'dark' | 'system'
  setTheme: (t: 'light' | 'dark' | 'system') => void

  filePanel: FilePanelState
  /** Open a single artifact (from a file card / chip). */
  setFilePreview: (f: FileArtifact | null) => void
  /** Open the chat's files list (topbar files action). */
  openFilesList: (files: FileArtifact[]) => void
  openFileFromList: (f: FileArtifact) => void
  backToList: () => void

  queryPanel: QueryPanelState
  /** A code block's Run button — (re-)opens the panel and bumps `nonce` so
   * the panel's effect restarts the run even for identical SQL. */
  runQuery: (req: QueryRunRequest) => void
  closeQueryPanel: () => void
}

export const useUIStore = create<UIState>((set) => ({
  sidebarCollapsed: false,
  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
  setSidebarCollapsed: (v) => set({ sidebarCollapsed: v }),

  commandOpen: false,
  commandNonce: 0,
  // Concept openPalette(): opening also expands a collapsed sidebar.
  setCommandOpen: (v) =>
    set((s) =>
      v
        ? {
            commandOpen: true,
            commandNonce: s.commandNonce + 1,
            sidebarCollapsed: false,
          }
        : { commandOpen: false },
    ),

  settingsOpen: false,
  setSettingsOpen: (v) => set({ settingsOpen: v }),
  settingsSection: 'General',
  setSettingsSection: (s) => set({ settingsSection: s }),

  model: 'sonnet-5',
  setModel: (name) => set({ model: name }),
  thinking: false,
  toggleThinking: () => set((s) => ({ thinking: !s.thinking })),

  research: false,
  toggleResearch: () => set((s) => ({ research: !s.research })),
  skills: [],
  toggleSkill: (name) =>
    set((s) => ({
      skills: s.skills.includes(name)
        ? s.skills.filter((n) => n !== name)
        : [...s.skills, name],
    })),

  capabilities: { fileUploads: true, screenshots: true },
  toggleCapability: (key) =>
    set((s) => ({
      capabilities: { ...s.capabilities, [key]: !s.capabilities[key] },
    })),

  language: 'en',
  setLanguage: (code) => set({ language: code }),

  theme:
    (localStorage.getItem('app-theme') as 'light' | 'dark' | 'system') ||
    'system',
  setTheme: (t) => {
    localStorage.setItem('app-theme', t)
    set({ theme: t })
  },

  filePanel: { mode: 'closed' },
  setFilePreview: (f) =>
    set({ filePanel: f ? { mode: 'file', file: f } : { mode: 'closed' } }),
  openFilesList: (files) => set({ filePanel: { mode: 'list', files } }),
  openFileFromList: (f) =>
    set((s) => ({
      filePanel: {
        mode: 'file',
        file: f,
        list: s.filePanel.mode === 'list' ? s.filePanel.files : undefined,
      },
    })),
  backToList: () =>
    set((s) => ({
      filePanel:
        s.filePanel.mode === 'file' && s.filePanel.list
          ? { mode: 'list', files: s.filePanel.list }
          : { mode: 'closed' },
    })),

  queryPanel: { mode: 'closed' },
  runQuery: (req) =>
    set((s) => ({
      queryPanel: {
        mode: 'open',
        nonce: (s.queryPanel.mode === 'open' ? s.queryPanel.nonce : 0) + 1,
        ...req,
      },
    })),
  closeQueryPanel: () => set({ queryPanel: { mode: 'closed' } }),
}))
